import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Sparkles, X } from "lucide-react";
import { FEATURE_ICONS, getIconByKey, pickFeatureIcons } from "@/lib/featureIcons";

/**
 * Per-feature icon picker.
 *
 * Compact 32x32 button that previews the icon currently in use, opens a
 * grid popover with every catalogue option, and lets the admin pick a
 * manual override (persisted as ``feature.icon_key``) OR reset back to
 * the keyword-driven auto-picker.
 *
 * The preview shows the *effective* icon at all times — i.e. if no
 * manual override is set, the auto-picker's choice is shown with a
 * subtle "AUTO" badge so the admin always knows what the flyer will
 * render.
 */
export default function FeatureIconPicker({
  feature,
  fallbackIcon,
  onChange,
  size = 32,
  className = "",
}) {
  const [open, setOpen] = useState(false);

  // Effective icon: manual override > caller-provided fallback (which is
  // typically the auto-picker's choice for this feature's position) > a
  // safe generic from the catalogue.
  const overrideEntry = feature?.icon_key
    ? FEATURE_ICONS.find((c) => c.key === feature.icon_key)
    : null;
  const EffectiveIcon =
    (overrideEntry && overrideEntry.Icon) ||
    fallbackIcon ||
    getIconByKey("layout");
  const isAuto = !feature?.icon_key;

  const select = (key) => {
    onChange?.({ ...(feature || {}), icon_key: key });
    setOpen(false);
  };

  const reset = () => {
    onChange?.({ ...(feature || {}), icon_key: null });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            isAuto
              ? "Auto-picked icon — click to change"
              : `Icon: ${overrideEntry?.label || feature.icon_key} — click to change`
          }
          title={isAuto ? "Auto-picked icon — click to choose manually" : overrideEntry?.label}
          className={`relative shrink-0 rounded border border-[#DDE5EC] bg-white hover:border-[#0088CC]/60 hover:bg-[#E6F2FA]/40 transition-colors flex items-center justify-center text-[#33475C] ${className}`}
          style={{ width: size, height: size }}
          data-testid="feature-icon-trigger"
        >
          {EffectiveIcon && <EffectiveIcon className="h-4 w-4" />}
          {isAuto && (
            <span
              className="absolute -bottom-1 -right-1 text-[7px] leading-none font-display font-semibold uppercase tracking-wider text-[#0088CC] bg-white border border-[#0088CC]/40 rounded px-1 py-px"
              aria-hidden="true"
            >
              AUTO
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[320px] p-3 bg-white border-[#DDE5EC] text-[#011836]"
        data-testid="feature-icon-popover"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5B6B7C] font-display">
            Choose an icon
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close icon picker"
            className="text-[#5B6B7C] hover:text-[#011836]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={reset}
          className={`w-full mb-2 flex items-center gap-2 px-2 py-1.5 rounded text-xs border transition-colors ${
            isAuto
              ? "border-[#0088CC]/60 bg-[#E6F2FA]/50 text-[#0088CC]"
              : "border-[#DDE5EC] text-[#33475C] hover:border-[#0088CC]/40 hover:bg-[#E6F2FA]/30"
          }`}
          data-testid="feature-icon-auto"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Auto-pick from keywords {isAuto && "(current)"}
        </button>
        <div
          className="grid grid-cols-6 gap-1.5 max-h-[260px] overflow-y-auto pr-1"
          data-testid="feature-icon-grid"
        >
          {FEATURE_ICONS.map((entry) => {
            const selected = feature?.icon_key === entry.key;
            const { Icon } = entry;
            return (
              <button
                key={entry.key}
                type="button"
                onClick={() => select(entry.key)}
                title={entry.label}
                aria-label={entry.label}
                aria-pressed={selected}
                className={`aspect-square rounded flex items-center justify-center transition-colors ${
                  selected
                    ? "bg-[#0088CC]/20 border border-[#0088CC] text-white"
                    : "border border-[#DDE5EC] text-[#33475C] hover:border-[#0088CC]/50 hover:bg-[#E6F2FA]/40"
                }`}
                data-testid={`feature-icon-option-${entry.key}`}
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
